import { IPaymentOfferModel, UserStore } from "@nf/common";
import { ROUTES } from "@nf/constants";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import cx from "classnames";

import {
  useCurrentSubscription,
  useOffers,
  useRouter,
  useSwitchOffer,
} from "../../../../hooks";
import PaymentConstrainedWidth from "../PaymentConstrainedWidth";
import PaymentTitle from "../PaymentTitle";

import styles from "../ChooseSubscription/ChooseSubscription.module.scss";

const OfferCard = dynamic(() => import("components/OfferCard"));

const ChangeSubscriptionDialog = dynamic(
  () =>
    import(
      "components/CancellationDialog/steps/Confirmation/ChangeSubscriptionDialog"
    )
);

const DotsLoader = dynamic(() => import("components/DotsLoader"));

export type ChangeSubscriptionProps = {
  offers?: IPaymentOfferModel[];
};

export const ChangeSubscription: React.FC<ChangeSubscriptionProps> = () => {
  const [selectedOffer, setSelectedOffer] =
    React.useState<IPaymentOfferModel>();
  const currentSubscription = useCurrentSubscription();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation("translations");

  const { offers, areOffersLoading } = useOffers();

  const {
    mutate: switchOffer,
    isLoading,
    error,
  } = useSwitchOffer({
    onSuccess: () => {
      dispatch(UserStore.Actions.getUserSubscriptions());

      router.replace(ROUTES.SETTINGS_SUBSCRIPTION, undefined, {
        withLoadingSplash: true,
      });
    },
  });

  const selectOffer = useCallback(
    (offer: IPaymentOfferModel) => {
      if (currentSubscription?.OfferId === offer.Id) {
        return router.replace(ROUTES.SETTINGS_SUBSCRIPTION);
      }

      setSelectedOffer(offer);
    },
    [currentSubscription?.OfferId]
  );

  const onCancel = () => setSelectedOffer(undefined);

  const onConfirm = () => {
    if (currentSubscription?.Id && selectedOffer?.Id) {
      switchOffer({
        subscription_id: currentSubscription.Id,
        to_offer_id: selectedOffer.Id,
      });
    }
  };

  if (!offers || areOffersLoading) {
    return <DotsLoader />;
  }

  const hasTwoOffers = !!(offers?.length && offers?.length === 2);

  return (
    <div className={styles.container}>
      <PaymentConstrainedWidth>
        <PaymentTitle>
          {t("web_payment_choose_subscription_title")}
        </PaymentTitle>

        <p className={styles.description}>
          {!!offers.length
            ? t("web_payment_choose_subscription_description")
            : t("web_payment_choose_subscription_error")}
        </p>
      </PaymentConstrainedWidth>

      <PaymentConstrainedWidth
        className={cx(styles.offers, {
          [styles.wide]: !hasTwoOffers,
        })}
      >
        {offers.map((offer) => (
          <OfferCard
            key={offer.Id}
            offer={offer}
            active={offer.Id === currentSubscription?.OfferId}
            wide={hasTwoOffers}
            handleSelect={selectOffer}
          />
        ))}
      </PaymentConstrainedWidth>

      {selectedOffer && (
        <ChangeSubscriptionDialog
          offer={selectedOffer}
          isLoading={isLoading}
          error={error}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
};

export default ChangeSubscription;
